// src/pages/Platform/PlatformUsers.js
//
// The Super Admin's own team-management screen — every PlatformUser
// (handlers.ListPlatformUsersHandler, task #202), with add/edit/delete
// through AddEditPlatformUser.js's modal. Same plain-markup approach as
// Companies.js (see that file's own doc comment on why).
import { useState, useEffect, useCallback, useContext } from "react";
import { getPlatformUsers, getPlatformGroups, deletePlatformUser } from "../../services/api";
import { PlatformAuthContext } from "../../context/PlatformAuthContext";
import AddEditPlatformUser from "./AddEditPlatformUser";

export default function PlatformUsers() {
  const { token, platformUser: me } = useContext(PlatformAuthContext);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, g] = await Promise.all([
        getPlatformUsers(token, search ? { search } : undefined),
        getPlatformGroups(token),
      ]);
      setUsers(u || []);
      setGroups(g || []);
    } catch (err) {
      console.error("Failed to load platform users:", err.message);
    } finally {
      setLoading(false);
    }
  }, [token, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (u) => {
    if (!window.confirm(`Remove ${u.email} from platform staff?`)) return;
    try {
      await deletePlatformUser(token, u.id);
      load();
    } catch (err) {
      alert(err.message || "Failed to delete platform user");
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>Platform users</h2>
        <button
          style={primaryBtnStyle}
          onClick={() => {
            setEditingUser(null);
            setShowModal(true);
          }}
        >
          + Add user
        </button>
      </div>

      <input
        style={{ ...inputStyle, maxWidth: 260, marginBottom: 16 }}
        placeholder="Search by name or email..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc", textAlign: "left" }}>
              {["Name", "Email", "Groups", "Status", ""].map((h, i) => (
                <th key={i} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={tdEmptyStyle}>Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} style={tdEmptyStyle}>No platform users found.</td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={tdStyle}>{u.first_name} {u.last_name}{u.id === me?.id ? " (you)" : ""}</td>
                  <td style={tdStyle}>{u.email}</td>
                  <td style={tdStyle}>{(u.groups || []).map((g) => g.name).join(", ") || "—"}</td>
                  <td style={tdStyle}>{u.is_active ? "Active" : "Inactive"}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <button
                      style={linkBtnStyle}
                      onClick={() => {
                        setEditingUser(u);
                        setShowModal(true);
                      }}
                    >
                      Edit
                    </button>
                    {u.id !== me?.id && (
                      <button style={{ ...linkBtnStyle, color: "#dc2626" }} onClick={() => handleDelete(u)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <AddEditPlatformUser
          token={token}
          existingUser={editingUser}
          groups={groups}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            load();
          }}
        />
      )}
    </div>
  );
}

const thStyle = { padding: "12px 16px", fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase" };
const tdStyle = { padding: "14px 16px", fontSize: 14, color: "#334155", verticalAlign: "top" };
const tdEmptyStyle = { padding: 40, textAlign: "center", color: "#94a3b8" };
const inputStyle = { padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, width: "100%", boxSizing: "border-box" };
const primaryBtnStyle = { background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" };
const linkBtnStyle = { background: "none", border: "none", color: "#2563eb", fontSize: 13, fontWeight: 600, cursor: "pointer", marginLeft: 10 };
